import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import "../css/main.scss";
import productData, {
  formatCurrency,
  siteConfig,
} from "../Datas/productData";

function Main() {
  const [activeSlide, setActiveSlide] = useState(0);
  const featuredProducts = productData.slice(0, 4);

  const slides = useMemo(
    () => [
      {
        eyebrow: "Paper goods for everyday joy",
        title: "Small notes. Big feelings.",
        copy: "Playful paper goods, thoughtfully made for the people and moments worth celebrating.",
        image: "/main/home-hero.jpg",
        mobileImage: "/main/home-hero-mobile.jpg",
        cta: "Shop greeting cards",
        to: "/shop?category=Greeting%20Cards",
      },
      {
        eyebrow: "Fresh from the studio",
        title: "Pocket-sized cheer",
        copy: "Colorful notes and tiny keepsakes designed to brighten desks, lunchboxes, and days.",
        image: productData[8]?.images?.[0] || "/main/home-hero.jpg",
        mobileImage: productData[8]?.images?.[0] || "/main/home-hero.jpg",
        cta: "Discover new arrivals",
        to: "/shop?tag=new",
      },
    ],
    []
  );

  useEffect(() => {
    document.title = `${siteConfig.brandName} — Paper goods that delight`;
  }, []);

  const goToSlide = (index) => {
    const nextIndex = (index + slides.length) % slides.length;
    setActiveSlide(nextIndex);
  };

  const promoCards = [
    {
      title: "Notes worth keeping",
      label: "Pocket Notes",
      to: "/shop?category=Pocket%20Notes",
      image: productData[8]?.images?.[0],
      tone: "light",
    },
    {
      title: "A tiny world of play",
      label: "Play Notepads",
      to: "/shop?category=Notepads",
      image: productData[12]?.images?.[0],
      tone: "dark",
    },
    {
      title: "Send a little sunshine",
      label: "Greeting Cards",
      to: "/shop?category=Greeting%20Cards",
      image: productData[3]?.images?.[0],
      tone: "dark",
    },
    {
      title: "Stick with happy",
      label: "Vinyl Stickers",
      to: "/shop?category=Stickers",
      image: productData[16]?.images?.[0],
      tone: "light",
    },
  ];

  const categoryTiles = [
    {
      title: "Play Notepads",
      to: "/shop?category=Notepads",
      image: productData[13]?.images?.[0],
      tint: "lime",
    },
    {
      title: "New Arrivals",
      to: "/shop?tag=new",
      image: productData[5]?.images?.[0],
      tint: "rose",
    },
    {
      title: "Happy Stickers",
      to: "/shop?category=Stickers",
      image: productData[17]?.images?.[0],
      tint: "aqua",
    },
  ];

  return (
    <main className="home-page" id="main-content">
      <section className="home-hero" aria-label="Featured collections">
        <div className="home-hero__viewport">
          {slides.map((slide, index) => (
            <article
              className={`home-hero__slide ${
                activeSlide === index ? "is-active" : ""
              }`}
              key={slide.title}
              aria-hidden={activeSlide !== index}
            >
              <picture className="home-hero__media">
                <source media="(max-width: 600px)" srcSet={slide.mobileImage} />
                <img
                  className="home-hero__image"
                  src={slide.image}
                  alt="A cheerful arrangement of original paper goods"
                />
              </picture>
              <div className="home-hero__wash" />
              <div className="home-hero__content">
                <p className="eyebrow">{slide.eyebrow}</p>
                <h1>{slide.title}</h1>
                <p className="home-hero__copy">{slide.copy}</p>
                <Link
                  className="button button--light"
                  to={slide.to}
                  tabIndex={activeSlide === index ? 0 : -1}
                >
                  {slide.cta}
                </Link>
              </div>
            </article>
          ))}

          <button
            className="home-hero__arrow home-hero__arrow--prev"
            type="button"
            onClick={() => goToSlide(activeSlide - 1)}
            aria-label="Previous feature"
          >
            <FiArrowLeft />
          </button>
          <button
            className="home-hero__arrow home-hero__arrow--next"
            type="button"
            onClick={() => goToSlide(activeSlide + 1)}
            aria-label="Next feature"
          >
            <FiArrowRight />
          </button>
        </div>

        <div className="home-hero__dots" aria-label="Choose a feature">
          {slides.map((slide, index) => (
            <button
              type="button"
              key={slide.title}
              className={activeSlide === index ? "is-active" : ""}
              onClick={() => goToSlide(index)}
              aria-label={`Show feature ${index + 1}`}
              aria-current={activeSlide === index ? "true" : undefined}
            />
          ))}
        </div>
      </section>

      <section className="home-section home-featured" aria-labelledby="new-heading">
        <div className="section-title-rule">
          <h2 id="new-heading">
            New + <em>noteworthy</em>
          </h2>
        </div>
        <div className="home-featured__grid">
          {featuredProducts.map((product) => (
            <Link
              className="home-product"
              to={`/shopDetails/${product.id}`}
              key={product.id}
            >
              <span className="home-product__image-wrap">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  loading="lazy"
                />
              </span>
              <span className="home-product__name">{product.name}</span>
              <span className="home-product__price">
                {formatCurrency(product.price)}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="home-promos" aria-label="Shop featured collections">
        {promoCards.map((card) => (
          <Link
            className={`home-promo home-promo--${card.tone}`}
            to={card.to}
            key={card.label}
          >
            <img src={card.image} alt="" loading="lazy" />
            <span className="home-promo__shade" />
            <span className="home-promo__content">
              <span className="home-promo__title">{card.title}</span>
              <span className="home-promo__link">
                {card.label} <FiArrowRight aria-hidden="true" />
              </span>
            </span>
          </Link>
        ))}
      </section>

      <section className="home-categories" aria-label="Popular categories">
        {categoryTiles.map((tile) => (
          <Link
            className={`home-category home-category--${tile.tint}`}
            to={tile.to}
            key={tile.title}
          >
            <img src={tile.image} alt="" loading="lazy" />
            <span className="home-category__tint" />
            <span className="home-category__ribbon">{tile.title}</span>
          </Link>
        ))}
      </section>

    </main>
  );
}

export default Main;
